/**
 * @fileoverview Password-reset link verification query hook.
 * @description Wraps POST /identity/password/reset/verify in a TanStack query keyed by the token,
 * so the /reset-password page can decide between its loading, invalid-link and new-password views
 * before the user types anything. It's a query rather than a mutation because verifying is
 * read-only and repeatable on the backend (it never consumes or extends the link). Never retried:
 * an unusable link (1031) won't become usable on a second try, and retrying a rate-limited one
 * (1032) only makes that worse. Cached for the page's lifetime, so useResetPassword drops the
 * cached result once the link is spent — otherwise reopening a used link in the same tab would
 * still show the form.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { verifyPasswordReset } from '@/features/auth/services/password-reset.service'

import type { ApiError } from '@/shared/types/http.types'

/** Query-key prefix of every reset-link verification; useResetPassword drops them once spent. */
export const VERIFY_PASSWORD_RESET_QUERY_KEY = ['identity', 'password-reset', 'verify'] as const

/**
 * @description Verifies the given reset token, skipped entirely when there's no token.
 * @param {string} token - The token from the /reset-password URL.
 * @returns {ReturnType<typeof useQuery>} The query object (data, isPending, isError, error, ...).
 */
export function useVerifyPasswordReset(token: string) {
  return useQuery<Awaited<ReturnType<typeof verifyPasswordReset>>, ApiError>({
    queryKey: [...VERIFY_PASSWORD_RESET_QUERY_KEY, token],
    queryFn: () => verifyPasswordReset({ token }),
    enabled: token.length > 0,
    retry: false,
    staleTime: Infinity,
  })
}
